import * as React from "react";
import * as ToggleGroupPrimitive from "radix-ui/toggle-group";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

// Two looks for one primitive, which MUI spelled ToggleButtonGroup plus an sx override:
//
// - `segmented`: a grey track with the selected segment as a raised pill on it. The
//   Draft / Assembly switch and the appearance switch, so the pane has one segmented
//   control rather than two that drift apart.
// - `bordered`: a row of bordered cells, the selected one tinted. A form choice --
//   ButtonGroup, the editor toolbar.
//
// The variant and size are set on the group and read by each item through context, so
// an item never has to be told which look it is part of.

const groupVariants = cva("inline-flex items-stretch", {
  variants: {
    variant: {
      segmented: "gap-0.5 rounded-lg bg-grey-100 p-0.5",
      bordered: "rounded-lg border border-input",
    },
    orientation: { horizontal: "flex-row", vertical: "flex-col" },
    fullWidth: { true: "flex w-full", false: "" },
  },
  defaultVariants: { variant: "segmented", orientation: "horizontal", fullWidth: false },
});

const itemVariants = cva(
  "inline-flex min-w-0 items-center justify-center gap-1.5 text-[12px] font-medium whitespace-nowrap transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40 disabled:pointer-events-none disabled:text-disabled [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        segmented:
          "h-[26px] rounded-md px-2.5 text-muted-foreground hover:text-strong data-[state=on]:bg-card data-[state=on]:text-strong data-[state=on]:shadow-xs",
        // The accent on its own tint is under AA for a 12px label in both schemes
        // (4.04:1 light, 3.57:1 dark), so a selected label moves off the accent: to the
        // deeper accent in light (5.13:1) and the lighter one in dark (5.88:1).
        bordered:
          "h-8 px-2.5 text-muted-foreground not-first:border-l not-first:border-input first:rounded-l-[7px] last:rounded-r-[7px] hover:bg-accent data-[state=on]:bg-primary-tint data-[state=on]:font-semibold data-[state=on]:text-primary-dark dark:data-[state=on]:text-primary-light",
      },
      fullWidth: { true: "flex-1", false: "" },
    },
    defaultVariants: { variant: "segmented", fullWidth: false },
  }
);

type GroupVariant = NonNullable<VariantProps<typeof groupVariants>["variant"]>;

const ToggleGroupContext = React.createContext<{ variant: GroupVariant; fullWidth: boolean }>({
  variant: "segmented",
  fullWidth: false,
});

type ToggleGroupProps = React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Root> & {
  variant?: GroupVariant;
  fullWidth?: boolean;
};

const ToggleGroup = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Root>,
  ToggleGroupProps
>(
  (
    { className, variant = "segmented", fullWidth = false, orientation, children, ...props },
    ref
  ) => (
    <ToggleGroupPrimitive.Root
      ref={ref}
      data-slot="toggle-group"
      orientation={orientation}
      className={cn(groupVariants({ variant, orientation, fullWidth }), className)}
      {...props}
    >
      <ToggleGroupContext.Provider value={{ variant, fullWidth }}>
        {children}
      </ToggleGroupContext.Provider>
    </ToggleGroupPrimitive.Root>
  )
);
ToggleGroup.displayName = "ToggleGroup";

const ToggleGroupItem = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Item>
>(({ className, ...props }, ref) => {
  const { variant, fullWidth } = React.useContext(ToggleGroupContext);
  return (
    <ToggleGroupPrimitive.Item
      ref={ref}
      data-slot="toggle-group-item"
      className={cn(itemVariants({ variant, fullWidth }), className)}
      {...props}
    />
  );
});
ToggleGroupItem.displayName = "ToggleGroupItem";

export { ToggleGroup, ToggleGroupItem };
